import { postJSON } from 'api/fetch';
import { pollBatchStatus } from 'api/workerStatus';
import { startsWith } from 'lodash';

import * as modalsActions from 'actions/modals';
import { approveAvailabilityAlert } from 'actions/notifications';
import * as settingsActions from 'actions/settings';

import * as sessionSelectors from 'selectors/session';

import {
  buildTeamAppTeamCalendlyUrl,
  calendlyUrlBuilderCommand,
} from 'features/calendlyEnterprise';
import { toggleMessengerShown } from 'features/messengerv2/actions';
import { VIEWS } from 'features/messengerv2/constants';
import { setCurrentChatView } from 'features/messengerv2/slice';
import { trackTeamAvailabilityUxEvents } from 'features/scheduleBuilder/tracking';

import getIsEmbedded from 'util/embedded';
import GoogleMaps from 'util/googleMaps';
import { hardRefresh as hardRefreshUtil } from 'util/hardRefresh';
import { printContent } from 'util/printContent';
import { browserHistory } from 'util/router';
import {
  facebookTrackEvent,
  trackKinesisEvent,
  trackPartnerIntegrationEvent,
  trackSignUpEvent,
  trackUxEvent,
} from 'util/tracking';

// Rails route :: React-Router route
const ROUTE_MAP = {
  '/employees': '/team',
  '/user/edit': '/settings/personal_info',
  '/reports': '/reports',
  '/hiring/dashboard': '/hiring/dashboard',
  '/billing': '/settings/billing',
};

const loadAssets = (assets = [], callback) => {
  const asset = assets.shift();

  if (!asset) {
    // We've loaded all of the assets and can actually render the page
    return callback();
  } else if (document.querySelectorAll(`script[src='${asset}']`).length) {
    // This asset has already been loaded, move on to the next one
    return loadAssets(assets, callback);
  }

  const script = document.createElement('script');
  script.setAttribute('src', asset);
  script.onload = () => loadAssets(assets, callback);

  document.head.appendChild(script);
};

class RailsBridge {
  constructor(store, routesData, rootMounter, $) {
    this.store = store;
    this.routesData = routesData;
    this.rootMounter = rootMounter;
    this.$ = $;

    this.googleMaps = new GoogleMaps(
      sessionSelectors.getGoogleMapsKey(this.reduxState())
    );
  }

  // If you want to avoid hard-refresh of your new React page when you navigate to it
  // you should add that url to `session/index.json.rabl` to `reactRoutes` node
  navigateToReactView(route) {
    const reactRoute = ROUTE_MAP[route] || route;
    const routeData = this.routesData.find(data =>
      startsWith(route, data.route)
    );

    if (!routeData) {
      this.hardRefresh(route);
      return;
    }

    loadAssets(routeData.assets || [], () => {
      browserHistory.push(reactRoute);
    });
  }

  reduxState() {
    return this.store.getState();
  }

  dispatch(action) {
    this.store.dispatch(action);
  }

  showPartnerEndOfIntegrationModal({ partnerName, shouldSetPassword = false }) {
    this.dispatch(
      modalsActions.showPartnerEndOfIntegrationModal({
        partnerName,
        shouldSetPassword,
      })
    );
  }

  showEndOfTrialModal(trialManagers, secondLocationName) {
    const onboardingTrialDaysLeft = sessionSelectors.getOnboardingTrialDaysLeft(
      this.reduxState()
    );

    const onboardingTrialSource = sessionSelectors.getOnboardingTrialSource(
      this.reduxState()
    );

    const shouldShowEndOfTrialModal =
      !['direct_mail_free_trial', 'reactivation_free_trial'].includes(
        onboardingTrialSource
      ) && onboardingTrialDaysLeft < 1;

    if (shouldShowEndOfTrialModal) {
      this.dispatch(
        modalsActions.showEndOfTrialListDialog({
          trialManagers,
          secondLocationName,
        })
      );
    }
  }

  showManagerCapsBeforeEndOfTrialModal(trialManagers) {
    this.dispatch(
      modalsActions.showManagerCapsBeforeEndOfTrialModal(trialManagers)
    );
  }

  showEndOfTrialManagerModal() {
    this.dispatch(modalsActions.showEndOfTrialManagerModal());
  }

  showRetrialOnboardingModal(steps, startAtStep = 0) {
    this.dispatch(modalsActions.showRetrialOnboardingModal(steps, startAtStep));
  }

  showSecondLocationTrialUpgradeModal() {
    this.dispatch(modalsActions.showSecondLocationTrialUpgradeModal());
  }

  approveAvailabilityAlert(id) {
    this.dispatch(approveAvailabilityAlert(id));
  }

  showLocationChooserModal({ locations }) {
    this.dispatch(modalsActions.showLocationChooserModal(locations));
  }

  showSquareSuccessModal() {
    this.dispatch(modalsActions.showSquareSuccessModal());
  }

  showGustoSuccessModal() {
    this.dispatch(modalsActions.showGustoSuccessModal());
  }

  showAdpCreditTipsModal(callback) {
    const hasType = sessionSelectors
      .getCurrentCompany(this.reduxState())
      .getIn(['adp_properties', 'credit_tips_type']);

    if (hasType) {
      callback();
    } else {
      const props = { alreadyConnected: true, handleConfirm: callback };

      if (window.Popup.visible()) {
        window.Popup.close();
      }

      this.dispatch(settingsActions.showAdpCreditTipsSettingsModal(props));
    }
  }

  pollBatchStatus = pollBatchStatus;

  hardRefresh(route) {
    hardRefreshUtil(route);
  }

  // MOUNT COMPONENTS

  trackKinesisEvent = trackKinesisEvent;

  trackUxEvent = trackUxEvent;

  trackSignUpEvent = trackSignUpEvent;

  trackTeamAvailabilityUxEvents = trackTeamAvailabilityUxEvents;

  trackPartnerIntegrationEvent = trackPartnerIntegrationEvent;

  facebookTrackEvent = facebookTrackEvent;

  showPayBaseModal(type) {
    this.dispatch(modalsActions.showPayBaseModal(type));
  }

  showAdpInterruptModal() {
    this.dispatch(
      modalsActions.showADPRunInterruptModal(this.postDisableAdpInterruptModal)
    );
  }

  postDisableAdpInterruptModal(callback) {
    postJSON('/adp/interrupt/disable', {}).then(callback);
  }

  // MESSAGING

  prefillMessengerData(data) {
    const {
      user_ids: prefilledUserIds,
      channel_token: channelId,
      data: prefilledMessage,
    } = data;

    this.dispatch(
      setCurrentChatView({
        channelId,
        prefilledUserIds,
        prefilledMessage,
        view: VIEWS[channelId ? 'SHOW' : 'ADD'],
      })
    );
    this.dispatch(toggleMessengerShown(true));
  }

  showAddExistingEmployeeInviteModal(attrs, numberOfRequests) {
    // Check if the new modal system rollout is enabled
    const payrollHandleUserMergeRequestPhase2Enabled =
      sessionSelectors.getPayrollHandleUserMergeRequestPhase2Enabled(
        this.reduxState()
      );

    if (payrollHandleUserMergeRequestPhase2Enabled) {
      // Use the new V2 modal system
      this.dispatch(
        modalsActions.showAddExistingEmployeeInviteModalV2(
          attrs,
          numberOfRequests
        )
      );
    } else {
      // Use the old modal system
      this.dispatch(
        modalsActions.showAddExistingEmployeeInviteModal(
          attrs,
          numberOfRequests
        )
      );
    }
  }

  calendlyUrl() {
    const currentUser = sessionSelectors.getCurrentUser(this.reduxState());

    return buildTeamAppTeamCalendlyUrl(
      calendlyUrlBuilderCommand({ currentUser })
    );
  }

  isEmbedded() {
    return getIsEmbedded();
  }

  showEulaModal() {
    this.dispatch(modalsActions.showEulaModal());
  }

  printContent = printContent;
}

export default (...opts) => new RailsBridge(...opts);
